import React, { FC } from 'react';
import { CustomTooltip } from '@components/CustomTooltip';
import { Box, Chip, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CheckIcon, InfoCircleEmptyIcon } from '@utils/iconsDefs';

export const SHARE_FILTERS_LABEL = 'Share filters';

export const SHARE_FILTERS_INFO_LABEL = 'About Share filters';

export const SHARE_FILTERS_TOOLTIP =
  'Keep the same filters across all modes (Controls, Business alignment). Off = each mode has its own filters.';

type Props = {
  checked: boolean;
  onChange: (checked: boolean) => void;
};

/**
 * The "Share filters" pill of the Filters root header, with the ⓘ beside it.
 *
 * Presentational: the root panel owns the flag and hands it in, so the pill
 * renders and is asserted without the store. It is exposed as a `switch`
 * rather than MUI's clickable-chip `button`, because it holds a two-state
 * setting and reads back as checked or not. Off is the theme's outlined
 * resting chip; on is the Figma "Share filters / On" state — the green-500
 * fill, the green-900 border and a 16px check — drawn with the same height
 * and padding so the header does not shift when it flips. The explanation
 * hangs on the ⓘ alone, as the design places it; the glyph is focusable and
 * named so the tooltip reaches the keyboard as well as the pointer.
 */
const ShareFiltersPill: FC<Props> = ({ checked, onChange }) => (
  <Stack direction="row" alignItems="center" gap="4px" flexShrink={0}>
    <Chip
      variant="grey-outlined"
      role="switch"
      aria-checked={checked}
      label={SHARE_FILTERS_LABEL}
      icon={checked ? <CheckIcon aria-hidden /> : undefined}
      onClick={() => onChange(!checked)}
      sx={{
        borderRadius: '128px',
        borderColor: checked ? themePalette.green[900] : themePalette.grey[700],
        bgcolor: checked ? themePalette.green[550] : 'transparent',
        '&:hover': {
          bgcolor: checked ? themePalette.green[600] : themePalette.grey[500],
        },
        '& .MuiChip-icon': {
          width: 16,
          height: 16,
          fill: themePalette.primary.main,
        },
      }}
    />

    <CustomTooltip
      title={SHARE_FILTERS_TOOLTIP}
      placement="right"
      sx={{ textAlign: 'center', maxWidth: 180 }}
    >
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={SHARE_FILTERS_INFO_LABEL}
        sx={{ display: 'inline-flex', lineHeight: 0, cursor: 'default' }}
      >
        <InfoCircleEmptyIcon
          width={20}
          height={20}
          fill={themePalette.grey[900]}
        />
      </Box>
    </CustomTooltip>
  </Stack>
);

export default ShareFiltersPill;
